import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { trackFormSubmit, trackWhatsAppClick, trackPhoneClick, trackEmailClick } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    equipment: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        // Track form submission in Google Analytics
        trackFormSubmit(formData.equipment);
        
        toast({
          title: "¡Mensaje Enviado!",
          description: response.data.message || "Nos pondremos en contacto contigo pronto.",
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          equipment: '',
          message: ''
        });
      } else {
        throw new Error(response.data.error || "Error desconocido");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Hubo un problema al enviar tu mensaje. Intenta nuevamente.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-20 bg-gray-200 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contáctanos
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Estamos listos para ayudarte con tu proyecto. Escríbenos o llámanos hoy mismo.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* WhatsApp CTA */}
            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 border-0 text-gray-900">
              <CardContent className="p-6">
                <MessageSquare className="w-12 h-12 mb-4" />
                <h3 className="text-2xl font-bold mb-2">¿Necesitas ayuda ahora?</h3>
                <p className="mb-4 text-orange-50">Chatea con nosotros por WhatsApp</p>
                <a 
                  href="https://wa.me/522292072733?text=Hola%2C%20necesito%20información%20sobre%20renta%20de%20equipo%20GENSOL"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={trackWhatsAppClick}
                  className="inline-block w-full"
                >
                  <Button className="w-full bg-white text-orange-600 hover:bg-gray-100">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Abrir WhatsApp
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Contact Details */}
            <Card className="bg-gray-100 border-gray-800">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start space-x-3">
                  <Phone className="w-5 h-5 text-orange-500 mt-1" />
                  <div>
                    <p className="text-gray-900 font-semibold">Teléfono</p>
                    <a href="tel:+522299212332" onClick={trackPhoneClick} className="text-gray-600 hover:text-orange-500 transition-colors">
                      229 921 23 32
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-orange-500 mt-1" />
                  <div>
                    <p className="text-gray-900 font-semibold">Email</p>
                    <a href="mailto:contacto@gensolver.com" onClick={trackEmailClick} className="text-gray-600 hover:text-orange-500 transition-colors">
                      contacto@gensolver.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-orange-500 mt-1" />
                  <div>
                    <p className="text-gray-900 font-semibold">Ubicación</p>
                    <p className="text-gray-600">Veracruz, México</p>
                    <p className="text-gray-500 text-sm">Servicio en sureste de México</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-gray-100 border-gray-800">
              <CardHeader>
                <CardTitle className="text-gray-900 text-2xl">Solicita una Cotización</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-900 text-sm font-medium mb-2 block">
                        Nombre Completo *
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Tu nombre"
                        required
                        className="bg-white border-gray-700 text-gray-900 placeholder:text-gray-500 focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label className="text-gray-900 text-sm font-medium mb-2 block">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="tu@email.com"
                        required
                        className="bg-white border-gray-700 text-gray-900 placeholder:text-gray-500 focus:border-orange-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-900 text-sm font-medium mb-2 block">
                        Teléfono *
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="229 921 23 32"
                        required
                        className="bg-white border-gray-700 text-gray-900 placeholder:text-gray-500 focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label className="text-gray-900 text-sm font-medium mb-2 block">
                        Equipo de Interés
                      </label>
                      <select
                        name="equipment"
                        value={formData.equipment}
                        onChange={handleChange}
                        className="w-full h-10 rounded-md border bg-white border-gray-700 text-gray-900 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Selecciona equipo</option>
                        <option value="generadores">Generadores</option>
                        <option value="torres-iluminacion">Torres de Iluminación</option>
                        <option value="soldadoras">Soldadoras</option>
                        <option value="varios">Varios equipos</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-gray-900 text-sm font-medium mb-2 block">
                      Mensaje *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Cuéntanos sobre tu proyecto y necesidades..."
                      required
                      rows={5}
                      className="bg-white border-gray-700 text-gray-900 placeholder:text-gray-500 focus:border-orange-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-gray-900 py-6 text-lg font-semibold"
                  >
                    {isSubmitting ? (
                      "Enviando..."
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Enviar Solicitud
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;